import type React from 'react';
import { createContext, useContext, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StatusPanelMode } from 'types';

const WALL_PARAM = 'wall';

interface StatusPanelContextValue {
  mode: StatusPanelMode;
  isWallScreen: boolean;
  setMode: (mode: StatusPanelMode) => void;
  toggleMode: () => void;
}

const StatusPanelContext = createContext<StatusPanelContextValue | null>(null);

interface StatusPanelProviderProps {
  children: React.ReactNode;
}

export const StatusPanelProvider: React.FC<StatusPanelProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isWallScreen = searchParams.get(WALL_PARAM) === 'true';
  const mode = isWallScreen ? StatusPanelMode.WallScreen : StatusPanelMode.Personal;

  const setMode = useCallback((newMode: StatusPanelMode) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newMode === StatusPanelMode.WallScreen) {
        next.set(WALL_PARAM, 'true');
      } else {
        next.delete(WALL_PARAM);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const toggleMode = useCallback(() => {
    setMode(isWallScreen ? StatusPanelMode.Personal : StatusPanelMode.WallScreen);
  }, [isWallScreen, setMode]);

  const value = useMemo(
    () => ({ mode, isWallScreen, setMode, toggleMode }),
    [mode, isWallScreen, setMode, toggleMode],
  );

  return (
    <StatusPanelContext.Provider value={value}>
      {children}
    </StatusPanelContext.Provider>
  );
};

export function useStatusPanelMode(): StatusPanelContextValue {
  const context = useContext(StatusPanelContext);
  if (!context) {
    throw new Error('useStatusPanelMode must be used within a StatusPanelProvider');
  }
  return context;
}
